import { motion } from "framer-motion";
import { Chapter, SectionHead } from "@/components/Reveal";
import {
  Route, Car, Building2, Landmark, Zap, Factory, Bot, TrafficCone,
} from "lucide-react";

const INDUSTRIES = [
  { icon: Route, name: "Roads & Mobility", text: "Continuously refreshed road and traffic intelligence.", hue: "from-signal-cyan/[0.14]", iconCls: "text-signal-cyan" },
  { icon: Car, name: "Vehicles", text: "Condition and context signals from the vehicles around us.", hue: "from-signal-violet/[0.14]", iconCls: "text-signal-violet" },
  { icon: Building2, name: "Buildings", text: "Physical state of the structures people rely on.", hue: "from-signal-green/[0.12]", iconCls: "text-signal-green" },
  { icon: Landmark, name: "Infrastructure", text: "Bridges, utilities and public assets, observed over time.", hue: "from-signal-coral/[0.12]", iconCls: "text-signal-coral" },
  { icon: Zap, name: "Energy", text: "Grid and electrical infrastructure visibility.", hue: "from-amber-400/[0.12]", iconCls: "text-amber-300" },
  { icon: Factory, name: "Industrial", text: "Sites, equipment and industrial environments.", hue: "from-signal-violet/[0.12]", iconCls: "text-signal-violet" },
  { icon: Bot, name: "Robotics", text: "Ground truth for machines that move through the world.", hue: "from-signal-cyan/[0.12]", iconCls: "text-signal-cyan" },
  { icon: TrafficCone, name: "Smart Cities", text: "A living data layer for urban operations.", hue: "from-signal-green/[0.12]", iconCls: "text-signal-green" },
];

export const Industries = () => {
  return (
    <section id="industries" className="relative py-28 sm:py-40 bg-ink-950" data-testid="section-industries">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter label="Industries" />
        <SectionHead
          title={"One network.\nMany industries."}
          sub="Designed for a worldwide physical data network - wherever the physical world needs to be understood."
          className="max-w-3xl"
        />

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((it, i) => (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative h-56 overflow-hidden rounded-xl bg-gradient-to-b ${it.hue} to-transparent p-6 flex flex-col justify-end transition-transform duration-500 hover:-translate-y-1`}
              data-testid={`industry-${it.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
            >
              <it.icon
                className={`absolute -right-4 -top-4 h-28 w-28 opacity-[0.08] transition-opacity duration-500 group-hover:opacity-[0.16] ${it.iconCls}`}
                aria-hidden="true"
              />
              <it.icon className={`h-6 w-6 mb-auto ${it.iconCls}`} />
              <h3 className="mt-8 font-display text-lg font-semibold tracking-tight text-mist">{it.name}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-steel">{it.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
